import {Component} from 'panel';
import {eventWasOutside, onAnimationEnd, offAnimationEnd} from 'common/util/dom';
import {registerMPElement} from 'common/util/register-element';
import {CHANGE_TYPE_REMOVED} from '../constants';

import template from './index.jade';
import css from './index.styl?inline';

const VISIBILITY_OPEN = `open`;
const VISIBILITY_OPENING = `opening`;
const VISIBILITY_CLOSED = `closed`;
const VISIBILITY_CLOSING = `closing`;

class SmartHubTooltip extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      alert: `json`,
      'alert-color': `string`,
      'explains-value': `string`,
      'offset-left': `number`,
      'offset-top': `number`,
      open: `boolean`,
      placement: `string`,
      'project-id': `string`,
      'utc-offset': `string`,
      'workspace-id': `number`,
    };
  }

  get config() {
    return {
      css,
      template,
      useShadowDom: true,
      defaultState: {
        visibility: VISIBILITY_CLOSED,
      },
      helpers: {
        handleChangedAlert: (ev) => {
          ev.stopPropagation();
          const {alert} = ev.detail;
          this.setAttribute(`alert`, JSON.stringify(alert));

          if (ev.detail.type === CHANGE_TYPE_REMOVED) {
            this.setAttribute(`open`, false);
          }

          ev.stopPropagation();
          this.dispatchEvent(new CustomEvent(ev.type, ev));
        },
        handleClick: (ev) => {
          ev.preventDefault();
          ev.stopPropagation();
        },
        handleUpdatedTooltip: () => this.updateTooltipPosition(),
        isVisible: () => this.state.visibility === VISIBILITY_OPEN,
      },
    };
  }

  _closeTooltip() {
    switch (this.state.visibility) {
      case VISIBILITY_CLOSED:
      case VISIBILITY_CLOSING:
        break;
      case VISIBILITY_OPENING:
        this.update({visibility: VISIBILITY_CLOSED});
        this.dispatchCurrentVisibility();
        break;
      case VISIBILITY_OPEN:
        this.update({visibility: VISIBILITY_CLOSING});
        this.dispatchCurrentVisibility();
        break;
    }
  }

  _openTooltip() {
    switch (this.state.visibility) {
      case VISIBILITY_OPEN:
      case VISIBILITY_OPENING:
        break;
      case VISIBILITY_CLOSING:
        this.update({visibility: VISIBILITY_OPEN});
        this.dispatchCurrentVisibility();
        break;
      case VISIBILITY_CLOSED:
        this.update({visibility: VISIBILITY_OPENING});
        this.dispatchCurrentVisibility();
        break;
    }
  }

  dispatchCurrentVisibility() {
    this.dispatchEvent(new CustomEvent(`change`, {detail: {state: this.state.visibility}}));
  }

  connectedCallback() {
    super.connectedCallback(...arguments);

    if (this.attr(`open`)) {
      this._openTooltip();
    }
    this._onAnimationEnd = () => {
      switch (this.state.visibility) {
        case VISIBILITY_OPENING:
          this.update({visibility: VISIBILITY_OPEN});
          document.addEventListener(`click`, this.clickOutsideListener);
          this.dispatchCurrentVisibility();
          break;
        case VISIBILITY_CLOSING:
          this.update({visibility: VISIBILITY_CLOSED});
          document.removeEventListener(`click`, this.clickOutsideListener);
          this.dispatchCurrentVisibility();
          break;
      }
    };
    onAnimationEnd(this.el, this._onAnimationEnd);

    // close when the user clicks outside of the tooltip
    this.clickOutsideListener = (ev) => {
      if (this.attr(`open`) && eventWasOutside(ev, this)) {
        this.setAttribute(`open`, false);
      }
    };
  }

  disconnectedCallback() {
    super.disconnectedCallback(...arguments);
    offAnimationEnd(this.el, this._onAnimationEnd);
    document.removeEventListener(`click`, this.clickOutsideListener);
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);

    if (!this.initialized) {
      return;
    }

    if (attr === `open`) {
      if (this.attr(`open`)) {
        this._openTooltip();
      } else {
        this._closeTooltip();
      }
    }
  }

  updateTooltipPosition() {
    const placement = this.attr(`placement`);
    const rect = this.getBoundingClientRect();
    const offsetTop = this.attr(`offset-top`);
    const offsetLeft = this.attr(`offset-left`);
    if (placement === `top`) {
      this.style.top = `${offsetTop - rect.height}px`;
      this.style.left = `${offsetLeft - rect.width / 2}px`;
    } else if (placement === `bottom`) {
      this.style.top = `${offsetTop}px`;
      this.style.left = `${offsetLeft - rect.width / 2}px`;
    } else if (placement === `left`) {
      this.style.top = `${offsetTop - rect.height / 2}px`;
      this.style.left = `${offsetLeft - rect.width}px`;
    } else if (placement === `right`) {
      this.style.top = `${offsetTop - rect.height / 2}px`;
      this.style.left = `${offsetLeft}px`;
    } else {
      console.error(`Unsupported placement: ${placement}`);
    }
  }
}

export default registerMPElement(`mp-smart-hub-tooltip`, SmartHubTooltip);
